import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    CircularProgress,
    Tooltip
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Lock as LockIcon } from '@mui/icons-material';
import { authService } from '../../services/authService';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Timestamp } from 'firebase/firestore';

interface Admin {
    id: string;
    email: string;
    name: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string | null;
}

const AdminsPage: React.FC = () => {
    const [admins, setAdmins] = useState<Admin[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [newAdmin, setNewAdmin] = useState({
        email: '',
        password: '',
        name: ''
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });

    const [passwordDialog, setPasswordDialog] = useState<{
        open: boolean;
        currentPassword: string;
        newPassword: string;
        confirmPassword: string;
        error: string | null;
    }>({
        open: false,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        error: null
    });

    useEffect(() => {
        loadAdmins();
    }, []);

    const loadAdmins = async () => {
        try {
            setLoading(true);
            const adminList = await authService.getAdmins();
            const formattedAdmins: Admin[] = adminList.map(admin => ({
                id: admin.id,
                email: admin.email,
                name: admin.name,
                isActive: admin.isActive,
                createdAt: admin.createdAt instanceof Timestamp 
                    ? admin.createdAt.toDate().toISOString() 
                    : new Date().toISOString(),
                updatedAt: admin.updatedAt instanceof Timestamp 
                    ? admin.updatedAt.toDate().toISOString() 
                    : null
            }));
            setAdmins(formattedAdmins);
        } catch (error) {
            console.error('Error loading admins:', error);
            setError('Yönetici listesi yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setNewAdmin({ email: '', password: '', name: '' });
    };

    const handleAddAdmin = async () => {
        try {
            await authService.addAdmin(newAdmin.email);
            setSnackbar({
                open: true,
                message: 'Yönetici başarıyla eklendi',
                severity: 'success'
            });
            handleCloseDialog();
            loadAdmins();
        } catch (error) {
            console.error('Error adding admin:', error);
            setSnackbar({
                open: true,
                message: 'Yönetici eklenirken bir hata oluştu',
                severity: 'error'
            });
        }
    };

    const handleRemoveAdmin = async (userId: string) => {
        try {
            await authService.updateAdminStatus(userId, false);
            setSnackbar({
                open: true,
                message: 'Yönetici yetkisi başarıyla kaldırıldı',
                severity: 'success'
            });
            loadAdmins();
        } catch (error) {
            console.error('Error removing admin:', error);
            setSnackbar({
                open: true,
                message: 'Yönetici yetkisi kaldırılırken bir hata oluştu',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handlePasswordChange = async () => {
        try {
            const { currentPassword, newPassword, confirmPassword } = passwordDialog;

            // Validation checks
            if (!currentPassword || !newPassword || !confirmPassword) {
                setPasswordDialog(prev => ({ ...prev, error: 'Lütfen tüm alanları doldurun' }));
                return;
            }

            if (newPassword !== confirmPassword) {
                setPasswordDialog(prev => ({ ...prev, error: 'Yeni şifreler eşleşmiyor' }));
                return;
            }

            if (newPassword.length < 6) {
                setPasswordDialog(prev => ({ ...prev, error: 'Yeni şifre en az 6 karakter olmalıdır' }));
                return;
            }

            const user = authService.getCurrentUser();
            if (!user || !user.email) {
                throw new Error('Kullanıcı oturumu bulunamadı');
            }

            // Kullanıcıyı yeniden doğrula
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Şifreyi güncelle
            await updatePassword(user, newPassword);

            setSnackbar({
                open: true,
                message: 'Şifreniz başarıyla güncellendi',
                severity: 'success'
            });

            setPasswordDialog({
                open: false,
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
                error: null
            });

        } catch (error: any) {
            console.error('Şifre değiştirme hatası:', error);
            
            let errorMessage = 'Şifre değiştirirken bir hata oluştu';
            if (error.code === 'auth/wrong-password') {
                errorMessage = 'Mevcut şifreniz yanlış';
            }
            
            setPasswordDialog(prev => ({ ...prev, error: errorMessage }));
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'email',
            headerName: 'E-posta',
            flex: 1
        },
        {
            field: 'displayName',
            headerName: 'Ad Soyad',
            flex: 1
        },
        {
            field: 'actions',
            headerName: 'İşlemler',
            flex: 1,
            renderCell: (params) => {
                const currentUser = authService.getCurrentUser();
                const isCurrentUser = currentUser && currentUser.email === params.row.email;

                return isCurrentUser ? (
                    <Tooltip title="Şifre Değiştir">
                        <IconButton
                            onClick={() => setPasswordDialog(prev => ({ ...prev, open: true }))}
                            color="primary"
                        >
                            <LockIcon />
                        </IconButton>
                    </Tooltip>
                ) : null;
            }
        }
    ];

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Yönetici Yönetimi
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenDialog}
                >
                    Yeni Yönetici
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <DataGrid
                    rows={admins}
                    columns={columns}
                    loading={loading}
                    autoHeight
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 5,
                            },
                        },
                    }}
                    disableRowSelectionOnClick
                />
            </Paper>

            <Dialog open={dialogOpen} onClose={handleCloseDialog}>
                <DialogTitle>Yeni Yönetici Ekle</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="İsim"
                            fullWidth
                            value={newAdmin.name}
                            onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                        />
                        <TextField
                            label="E-posta"
                            type="email"
                            fullWidth
                            value={newAdmin.email}
                            onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                        />
                        <TextField
                            label="Şifre"
                            type="password"
                            fullWidth
                            value={newAdmin.password}
                            onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>İptal</Button>
                    <Button
                        onClick={handleAddAdmin}
                        variant="contained"
                        disabled={!newAdmin.email || !newAdmin.password || !newAdmin.name}
                    >
                        Ekle
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={passwordDialog.open}
                onClose={() => setPasswordDialog(prev => ({ ...prev, open: false, error: null }))}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Şifre Değiştir</DialogTitle>
                <DialogContent>
                    {passwordDialog.error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {passwordDialog.error}
                        </Alert>
                    )}
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Mevcut Şifre"
                        type="password"
                        fullWidth
                        value={passwordDialog.currentPassword}
                        onChange={(e) => setPasswordDialog(prev => ({ ...prev, currentPassword: e.target.value }))}
                    />
                    <TextField
                        margin="dense"
                        label="Yeni Şifre"
                        type="password"
                        fullWidth
                        value={passwordDialog.newPassword}
                        onChange={(e) => setPasswordDialog(prev => ({ ...prev, newPassword: e.target.value }))}
                    />
                    <TextField
                        margin="dense"
                        label="Yeni Şifre (Tekrar)"
                        type="password"
                        fullWidth
                        value={passwordDialog.confirmPassword}
                        onChange={(e) => setPasswordDialog(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPasswordDialog(prev => ({ ...prev, open: false, error: null }))}>
                        İptal
                    </Button>
                    <Button onClick={handlePasswordChange} variant="contained" color="primary">
                        Şifreyi Değiştir
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default AdminsPage; 